
import React, { useState, useEffect } from "react";
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  UserPlus,
  Edit
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { SubsidyBeneficiary, getAllBeneficiaries } from "@/lib/db/subsidyDb";
import { format } from "date-fns";

const EligibleBeneficiariesTab: React.FC = () => {
  const [beneficiaries, setBeneficiaries] = useState<SubsidyBeneficiary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterIncomeGroup, setFilterIncomeGroup] = useState<string>("all");
  const [filterMemberType, setFilterMemberType] = useState<string>("all");
  const { toast } = useToast();
  
  useEffect(() => {
    const loadBeneficiaries = async () => {
      setIsLoading(true);
      try {
        const data = await getAllBeneficiaries();
        setBeneficiaries(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading beneficiaries:", error);
        toast({
          variant: "destructive",
          title: "Failed to load beneficiaries",
          description: "Could not retrieve beneficiary data. Please try again."
        });
        setIsLoading(false);
      }
    };
    
    loadBeneficiaries();
  }, [toast]);
  
  const filteredBeneficiaries = beneficiaries
    .filter(beneficiary => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          beneficiary.name.toLowerCase().includes(searchLower) ||
          beneficiary.icNumber.toLowerCase().includes(searchLower) ||
          beneficiary.phone.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .filter(beneficiary => {
      // Apply income group filter
      if (filterIncomeGroup !== "all") {
        return beneficiary.incomeBracket.toLowerCase().includes(filterIncomeGroup.toLowerCase());
      }
      return true;
    })
    .filter(beneficiary => {
      // Apply member type filter
      if (filterMemberType !== "all") {
        return beneficiary.memberType.toLowerCase() === filterMemberType.toLowerCase();
      }
      return true;
    });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Eligible Beneficiaries</CardTitle>
              <CardDescription>
                View and manage people eligible for subsidy programs.
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Add Beneficiary
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, IC or phone..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={filterIncomeGroup} onValueChange={setFilterIncomeGroup}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by income" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Income Groups</SelectItem>
                    <SelectItem value="Below RM1000">Below RM1000</SelectItem>
                    <SelectItem value="RM1000-RM2000">RM1000-RM2000</SelectItem>
                    <SelectItem value="RM2000-RM3000">RM2000-RM3000</SelectItem>
                    <SelectItem value="RM3000-RM4000">RM3000-RM4000</SelectItem>
                    <SelectItem value="Above RM4000">Above RM4000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={filterMemberType} onValueChange={setFilterMemberType}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Member Types</SelectItem>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="Coop Member">Coop Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" title="Export data">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            filteredBeneficiaries.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-muted-foreground">No eligible beneficiaries found. Add a beneficiary to get started.</p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>IC Number</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Member Type</TableHead>
                      <TableHead>Income Bracket</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Last Subsidy</TableHead>
                      <TableHead>Total Used (RM)</TableHead>
                      <TableHead>Avg Spend (RM)</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBeneficiaries.map((beneficiary) => (
                      <TableRow key={beneficiary.beneficiaryId}>
                        <TableCell>{beneficiary.name}</TableCell>
                        <TableCell>{beneficiary.icNumber}</TableCell>
                        <TableCell>{beneficiary.phone}</TableCell>
                        <TableCell>{beneficiary.memberType}</TableCell>
                        <TableCell>{beneficiary.incomeBracket}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {beneficiary.tags.map((tag) => (
                              <Badge key={tag} variant="outline" className="whitespace-nowrap">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {beneficiary.lastSubsidyReceived
                            ? format(new Date(beneficiary.lastSubsidyReceived), "dd/MM/yyyy")
                            : "Never"}
                        </TableCell>
                        <TableCell>{beneficiary.totalSubsidyUsed.toFixed(2)}</TableCell>
                        <TableCell>{beneficiary.avgSpendPerVisit.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EligibleBeneficiariesTab;
